import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendTestEmail, verifiedEmailList } from '../../../../redux/actions/journeyAction';
import { fetchAssetLibraryHashtagList } from '../../../../redux/actions/actions';
import ReactMultiSelectCheckboxes from "react-multiselect-checkboxes";
import '../Custom.css';

const EmailDrawer = ({ isOpen, onClose, onSave, initialData, authToken, templates = [], loading = false }) => {
  
  const dispatch = useDispatch();
  const { sendEmailLoading, sendEmailSuccess } = useSelector(state => state.templates);
  
  // Get asset library data from Redux state
  const { hashtagList, loading: assetLibraryLoading } = useSelector(state => state.assetLibrary);
  
  // Debug props
  useEffect(() => {
    if (!isOpen) return;
    setCampaignName('');
    setEmail('');
    setCampaignTags('');
    setSelectedCampaignTags([]);
    setThrottling('immediate');
    setSelectedTemplate(null);
    setActiveTab('message');
  }, [isOpen]);

  // Fetch asset library hashtag data when drawer opens
  useEffect(() => {
    if (isOpen && authToken) {
      console.log('Fetching asset library hashtag data...');
      dispatch(fetchAssetLibraryHashtagList(authToken));
    }
  }, [isOpen, authToken, dispatch]);

  // Debug asset library data
  useEffect(() => {
    console.log('Asset Library Hashtag Data:', hashtagList);
    console.log('Asset Library Loading:', assetLibraryLoading);
  }, [hashtagList, assetLibraryLoading]);
  
  // Note: Auto-close functionality removed to prevent drawer from closing automatically
  
  // Helper function to get template field values
  const getTemplateValue = (template, fieldName) => {
    return template[fieldName] || template[fieldName.toLowerCase()] || template[fieldName.toUpperCase()] || '';
  };

  const getTemplateId = (template) => {
    // Convert to string for comparison since select values are strings
    const id = getTemplateValue(template, 'content_id') || 
               getTemplateValue(template, 'id') || 
               getTemplateValue(template, 'template_id') || 
               template.id || '';
    return String(id);
  };

  const getTemplateTitle = (template) => {
    return getTemplateValue(template, 'content_title') || 
           getTemplateValue(template, 'title') || 
           getTemplateValue(template, 'name') || 
           getTemplateValue(template, 'template_title') || 
           getTemplateValue(template, 'campaign_name') ||
           getTemplateValue(template, 'campaign_title') ||
           getTemplateValue(template, 'subject') ||
           template.title || template.name || template.campaign_name || template.subject || '';
  };

  const getTemplateMessage = (template) => {
    // Prioritize message field since that's what the API provides
    return template.message || 
           template.description || 
           template.content || 
           template.body || 
           template.html_content || 
           template.text_content || 
           template.template_content ||
           template.content_body ||
           template.email_content ||
           template.sms_content ||
           'No message content available';
  };

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Debug templates
  useEffect(() => {
    if (templates && templates.length > 0) {
    }
  }, [templates, loading]);

  const [campaignName, setCampaignName] = useState(initialData?.emailConfig?.campaignName || '');
  const [email, setEmail] = useState(initialData?.emailConfig?.testEmail || '');
  const [campaignTags, setCampaignTags] = useState(initialData?.emailConfig?.campaignTags || '');
  const [selectedCampaignTags, setSelectedCampaignTags] = useState([]);
  const [throttling, setThrottling] = useState(initialData?.emailConfig?.throttling || 'immediate');
  const [activeTab, setActiveTab] = useState('message');

  // Verified email state
  const [fromEmailOptions, setFromEmailOptions] = useState([]);
  const [replyEmailOptions, setReplyEmailOptions] = useState([]);
  const [fromEmail, setFromEmail] = useState('');
  const [replyEmail, setReplyEmail] = useState('');

  // Determine if current context/template is auto email
  const isAutoEmail = React.useMemo(() => {
    // Check node-level auto_email flag first (from API response)
    const flagFromNode = initialData?.auto_email;
    // Fallback to template or emailConfig flags
    const flagFromInitial = initialData?.emailConfig?.is_autoemail ?? initialData?.is_autoemail;
    const flagFromTemplate = selectedTemplate
      ? (selectedTemplate.auto_email ?? selectedTemplate.is_autoemail ?? selectedTemplate.autoemail)
      : undefined;
    
    // Priority: node flag > template flag > initial data flag
    const flag = flagFromNode !== undefined ? flagFromNode : 
                 (flagFromTemplate !== undefined ? flagFromTemplate : flagFromInitial);
    return flag === true || flag === 1 || flag === '1';
  }, [initialData, selectedTemplate]);

  // Fetch verified email list when drawer opens
  useEffect(() => {
    if (!isOpen || !authToken) return;
    (async () => {
      const result = await dispatch(verifiedEmailList(authToken));
      if (result?.success) {
        const from = Array.isArray(result.payload?.from) ? result.payload.from : [];
        const reply = Array.isArray(result.payload?.reply) ? result.payload.reply : [];
        setFromEmailOptions(from);
        setReplyEmailOptions(reply);
        if (!fromEmail && from.length > 0) setFromEmail(from[0]);
        if (!replyEmail && reply.length > 0) setReplyEmail(reply[0]);
      }
    })();
  }, [isOpen, authToken, dispatch]);

  // Format hashtag list for ReactMultiSelectCheckboxes (memoized to avoid new ref each render)
  const hashtagOptions = React.useMemo(() => (
    hashtagList && hashtagList.length > 0
      ? hashtagList.map(({ id: value, tag_name: label }) => ({ value, label }))
      : []
  ), [hashtagList]);
  
  // Email validation function for multiple emails
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    
    // If email is empty, return true (no validation error)
    if (email.trim() === '') return true;
    
    // Split by comma and trim whitespace
    const emails = email.split(',').map(e => e.trim()).filter(e => e.length > 0);
    
    // Check if all emails are valid
    return emails.every(email => emailRegex.test(email));
  };
  
  // Check if email is valid
  const isEmailValid = email.trim() === '' || validateEmail(email);

  const selectCampaignTagsHandler = (value, event) => {
    if (event.action === "select-option" && event.option.value === "*") {
      setSelectedCampaignTags(hashtagOptions);
    } else if (
      event.action === "deselect-option" &&
      event.option.value === "*"
    ) {
      setSelectedCampaignTags([]);
    } else if (event.action === "deselect-option") {
      value.filter((o) => o.value !== "*");
      setSelectedCampaignTags(value);
    } else if (value.length === hashtagOptions.length - 1) {
      setSelectedCampaignTags(hashtagOptions);
    } else {
      setSelectedCampaignTags(value);
    }
  };

  // Update state when initial data changes
  useEffect(() => {
    // Handle both node structure (from JourneyBuilder) and direct emailConfig structure
    const emailConfig = initialData?.data?.emailConfig || initialData?.emailConfig;
    const templateId = initialData?.data?.templateId || initialData?.templateId;
    
    if (emailConfig) {
      setCampaignName(emailConfig.campaignName || '');
      setEmail(emailConfig.testEmail || '');
      setCampaignTags(emailConfig.campaignTags || '');
      setThrottling(emailConfig.throttling || 'immediate');

      if (templates?.length > 0 && emailConfig.template) {
        const template = templates.find(t => getTemplateId(t) === String(emailConfig.template));
        if (template) setSelectedTemplate(template);
      }
    } else if (templateId) {
      // Fallback to templateId if emailConfig is not available
      if (templates?.length > 0) {
        const template = templates.find(t => getTemplateId(t) === String(templateId));
        if (template) setSelectedTemplate(template);
      }
    }
  }, [initialData, templates]);

  useEffect(() => {
    if (!isOpen) return;
    // Handle both node structure and direct emailConfig structure
    const emailConfig = initialData?.data?.emailConfig || initialData?.emailConfig;
    const initialIds = emailConfig?.campaignTags;
    if (Array.isArray(initialIds) && initialIds.length > 0 && hashtagOptions.length > 0) {
      const initialTags = hashtagOptions.filter(option => initialIds.includes(option.value));
      const isSame = selectedCampaignTags.length === initialTags.length &&
        selectedCampaignTags.every(t => initialTags.some(it => it.value === t.value));
      if (!isSame) setSelectedCampaignTags(initialTags);
    }
  }, [isOpen, hashtagOptions, initialData, selectedCampaignTags]);

  const handleNext = () => {
    // Validate template is required for Message tab
    if (activeTab === 'message' && !selectedTemplate) {
      alert('Template is required. Please select a template.');
      return;
    }
    
    // Validate Subject is required for Basic Info tab
    if (activeTab === 'basic' && !campaignName.trim()) {
      alert('Subject is required. Please enter a Subject.');
      return;
    }
    
    // Switch to next tab based on current tab
    if (activeTab === 'message') {
      setActiveTab('basic');
    } else if (activeTab === 'basic') {
      setActiveTab('test');
    }
  };

     const handleSend = () => {
     // Validate template is required
     if (!selectedTemplate) {
       alert('Template is required. Please select a template.');
       return;
     }
     
     // Validate Subject is required
     if (!campaignName.trim()) {
       alert('Subject is required. Please enter a Subject.');
       return;
     }
     
     // Validate email is required and valid
     if (!email.trim()) {
       alert('Email is required. Please enter an email address.');
       return;
     }
     
     // Use the multiple email validation function
     if (!validateEmail(email)) {
       alert('Please enter valid email address(es) separated by commas.');
       return;
     }
    
    const emailData = {
      campaignName,
      campaignTags: selectedCampaignTags.map(tag => tag.value),
      throttling,
      template: selectedTemplate ? getTemplateId(selectedTemplate) : null,
      testEmail: email,
      ...(isAutoEmail ? { fromEmail, replyEmail } : {}),
    };
    
    // Call Redux action to send test email
    dispatch(sendTestEmail(authToken, emailData));
    
    // Note: Removed onSave call to prevent drawer from closing after sending test email
  };

     const handleSave = () => {
      // Validate template is required
      if (!selectedTemplate) {
        alert('Template is required. Please select a template.');
        return;
      }
      
      // Validate Subject is required
      if (!campaignName.trim()) {
        alert('Subject is required. Please enter a Subject.');
        return;
      }
      const emailData = {
        campaignName,
        campaignTags: selectedCampaignTags.map(tag => tag.value),
        throttling,
        template: selectedTemplate ? getTemplateId(selectedTemplate) : null,
        testEmail: email,
        templateTitle: selectedTemplate ? getTemplateTitle(selectedTemplate) : null,
        ...(isAutoEmail ? { fromEmail, replyEmail } : {}),
      };
    
      // Call onSave to save the email configuration
      onSave(emailData);
      setActiveTab('message');
    // Note: Auto-close removed - drawer will stay open after saving
  };

  
  return (
    <>
      <style>
        {`
          /* Override any default ReactMultiSelectCheckboxes styles */
          .select-box-w100 {
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            position: relative !important;
          }
          
          /* Ultra aggressive width forcing for ReactMultiSelectCheckboxes */
          .select-box-w100 > div {
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            position: relative !important;
          }
          
          .select-box-w100 > div > div {
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            position: relative !important;
          }
          
          /* Override any ReactMultiSelectCheckboxes default styles */
          .select-box-w100 .rmsc {
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            position: relative !important;
          }
          
          /* Target the specific ReactMultiSelectCheckboxes component */
          .select-box-w100 .rmsc .dropdown-container {
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            position: relative !important;
          }
          
          .select-box-w100 .rmsc .dropdown-container .dropdown-heading {
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            position: relative !important;
          }
          
          .select-box-w100 .rmsc .dropdown-container .dropdown-content {
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            position: relative !important;
          }
          
          .select-box-w100 .rmsc .dropdown-container .dropdown-content .search {
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            position: relative !important;
          }
          
          .select-box-w100 .rmsc .dropdown-container .dropdown-content .options {
            width: 100% !important;
            max-width: 100% !important;
            display: block !important;
            position: relative !important;
          }
            .css-1r4vtzz {
              width: 100% !important;
              border: 1px solid hsl(0,0%,80%) !important;
              border-radius: 4px !important;
              box-shadow: none !important;
            }
            .css-48ayfv{
              width: 100% !important;
              border: 1px solid hsl(0,0%,80%) !important;
              border-radius: 4px !important;
              box-shadow: none !important;
            }
        `}
             </style>
       {/* Overlay */}
      {isOpen && <div className="drawer-overlay" onClick={onClose}></div>}

      {/* Drawer */}
      <div className={`drawer ${isOpen ? 'open' : ''}`}>
        {/* Header with Tabs */}
        <div className="drawer-header border-bottom d-flex justify-content-between align-items-center p-3">
          <div className="nav nav-pills">
            <button
              className={`nav-link ${activeTab === 'message' ? 'active' : ''}`}
              onClick={() => setActiveTab('message')}
            >
              Message
            </button>
            <button
              className={`nav-link ${activeTab === 'basic' ? 'active' : ''}`}
              // onClick={() => setActiveTab('basic')}
            >
              Basic Info
            </button>
            <button
              className={`nav-link ${activeTab === 'test' ? 'active' : ''}`}
              // onClick={() => setActiveTab('test')}
            >
              Test
            </button> 
          </div>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>

        {/* Content */}
        <div className="drawer-body p-4">
          
          {activeTab === 'basic' && (
            <>
              {/* Subject */}
              <div className="mb-3">
                <label className="form-label small">
                  Subject <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${campaignName.trim() === '' ? 'is-invalid' : ''}`}
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Enter Subject"
                  required
                />
                {campaignName.trim() === '' && (
                  <div className="invalid-feedback">
                    Subject is required.
                  </div>
                )}
              </div>

              {isAutoEmail && (
                <>
                  <div className="mb-3">
                    <label className="form-label small">From Email</label>
                    <select
                      className="form-select"
                      value={fromEmail}
                      onChange={(e) => setFromEmail(e.target.value)}
                    >
                      {fromEmailOptions.length === 0 && (
                        <option value="" disabled>Loading...</option>
                      )}
                      {fromEmailOptions.map((emailOpt) => (
                        <option key={emailOpt} value={emailOpt}>{emailOpt}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small">Reply-To Email</label>
                    <select
                      className="form-select"
                      value={replyEmail}
                      onChange={(e) => setReplyEmail(e.target.value)}
                    >
                      {replyEmailOptions.length === 0 && (
                        <option value="" disabled>Loading...</option>
                      )}
                      {replyEmailOptions.map((emailOpt) => (
                        <option key={emailOpt} value={emailOpt}>{emailOpt}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="mb-3">
                <label className="form-label small">Campaign Tags</label>
                <div 
                  className="select-box-w100" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '100%', 
                    display: 'block',
                    position: 'relative'
                  }}
                >
                  <div 
                    style={{ 
                      width: '100%', 
                      maxWidth: '100%', 
                      display: 'block',
                      position: 'relative'
                    }}
                  >
                    <ReactMultiSelectCheckboxes
                      options={hashtagOptions}
                      placeholderButtonLabel={
                        hashtagList.length === 0 
                          ? "Loading Campaign Tags..." 
                          : selectedCampaignTags?.length > 0 
                            ? `${selectedCampaignTags.length} selected` 
                            : "Select Campaign Tags"
                      }
                      defaultValue={selectedCampaignTags}
                      values={selectedCampaignTags}
                      setState={setSelectedCampaignTags}
                      onChange={selectCampaignTagsHandler}
                      disabled={assetLibraryLoading}
                      style={{ 
                        width: '100%', 
                        maxWidth: '100%', 
                        display: 'block',
                        position: 'relative'
                      }}
                    />
                  </div>
                </div>
                <div className="form-text small">
                  This field is optional. Tags help you analyze campaign results for a group of campaigns that belong to a tag.
                </div>
              </div>

              

              {/* Throttling */}
              {/* <div className="mb-3">
                <label className="form-label small d-block">Throttling</label>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    value="immediate"
                    checked={throttling === 'immediate'}
                    onChange={(e) => setThrottling(e.target.value)}
                  />
                  <label className="form-check-label">
                    Do not throttle campaigns. Send all messages immediately
                  </label>
                </div>

                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    value="channel"
                    checked={throttling === 'channel'}
                    onChange={(e) => setThrottling(e.target.value)}
                  />
                  <label className="form-check-label">
                    Throttle campaigns as per channel throttling limits (200,000 messages per minute)
                  </label>
                </div>

                <div className="form-check d-flex align-items-center">
                  <input
                    className="form-check-input"
                    type="radio"
                    value="custom"
                    checked={throttling === 'custom'}
                    onChange={(e) => setThrottling(e.target.value)}
                  />
                  <label className="form-check-label me-2">Throttle campaigns at</label>
                  {throttling === 'custom' && (
                    <input type="number" className="form-control w-auto me-2" placeholder="0" />
                  )}
                  <span>messages per minute</span>
                </div>
              </div> */}
            </>
          )}

          {activeTab === 'message' && (
            <>
              <div className="mb-3">
                <label className="form-label small">
                  Select Template <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${!selectedTemplate ? 'is-invalid' : ''}`}
                  value={selectedTemplate ? getTemplateId(selectedTemplate) : ''}
                  onChange={(e) => {
                    const templateId = e.target.value;
                    const template = templates.find(t => getTemplateId(t) === templateId);
                    setSelectedTemplate(template);
                  }}
                  disabled={loading}
                  required
                >
                 <option value="" disabled>
                   {loading ? "Loading templates..." : "Select..."}
                 </option>
                 {templates && templates.length > 0 ? (
                   templates.map((template, index) => (
                     <option key={index} value={getTemplateId(template)}>
                       {getTemplateTitle(template)}
                     </option>
                   ))
                 ) : (
                    <option value="" disabled>
                      No templates available
                    </option>
                  )}
                </select>
                {!selectedTemplate && (
                  <div className="invalid-feedback">
                    Template is required.
                  </div>
                )}
              </div>

              {selectedTemplate && (
                <div className="template-preview">
                  <p className="text-muted">Template Preview:</p>
                  <div className="border rounded p-3">
                    <div>
                     
                      
                      <div className="mt-3">
                       
                        <div className="mt-1 p-2 bg-light rounded">
                          <div dangerouslySetInnerHTML={{ __html: getTemplateMessage(selectedTemplate) }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

            {activeTab === 'test' && (
              <div>
                <label className="form-label small">
                  Enter Email <span className="text-danger">*</span>
                </label>
                <div className="d-flex">
                  <input
                    type="text"
                    className={`form-control me-2 ${email.trim() !== '' && !isEmailValid ? 'is-invalid' : ''}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email(s) separated by commas"
                  />
                  <button
                    className="btn btn-info text-white"
                    onClick={handleSend}
                    disabled={!email.trim() || !isEmailValid || sendEmailLoading}
                  >
                    {sendEmailLoading ? 'Sending...' : 'Send'}
                  </button>
                </div>
                 
                 {email.trim() !== '' && !isEmailValid && (
                   <div className="invalid-feedback d-block">
                     Please enter valid email address(es) separated by commas.
                   </div>
                 )}
              </div>
            )}
        </div>

         {/* Footer */}
         <div className="bg-light p-3 d-flex justify-content-end gap-2">
           {activeTab === 'message' && (
              <button 
                className="btn btn-info text-white" 
                onClick={handleNext}
                disabled={!selectedTemplate}
              >
                Next
              </button>
            )}
            {activeTab === 'basic' && (
              <div className="bg-light p-3 d-flex justify-content-end gap-2">
                <button className="btn btn-outline-secondary" onClick={() => setActiveTab('message')}>
                  Back
                </button>
                <button 
                  className="btn btn-info text-white" 
                  onClick={handleNext}
                  disabled={!campaignName.trim()}
                >
                  Next
                </button>
              </div>
            )}
             {activeTab === 'test' && (
               <div className="bg-light p-3 d-flex justify-content-end gap-2">
                 <button className="btn btn-secondary" onClick={() => setActiveTab('basic')}>
                   Back
                 </button>
                 <button 
                   type="button" 
                   className="btn btn-info text-white"
                   onClick={handleSave}
                 >
                   Save
                 </button>
               </div>
             )}
         </div>
      </div>
    </>
  );
};

export default EmailDrawer;
